import React, { useState, useEffect, useContext } from 'react';
import PortalHeader from "../components/PortalHeader.js";
import '../styles/portal.css';
import CustomTextField from '../utils/custom_textfield.js';
import CustomDropdown from '../utils/custom_dropdown.js';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import OtpInput from '../utils/otp_input.js';
import { Link, useNavigate } from 'react-router-dom'
import { AuthContext } from "../context/AuthContext.js";
import { LoadingComponent } from '../components/LoadingComponent.js';
import Alert from '../components/Alert.js';
import { extractUTMParameters, storeUTMParameters, getStoredUTMParameters, buildUrlWithUTM } from '../utils/utmUtils.js';
import Swal from 'sweetalert2';


const PortalRegisterPage = () => {
    const [otp, setOtp] = useState("");
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        dateOfBirth: null,
        qualification: '',
        yearOfPassing: ''
    });
    const authContext = useContext(AuthContext);
    const { authLoading } = useContext(AuthContext);
    const [showOtp, setShowOtp] = useState(false);
    const [error, setError] = useState(null);

    // Qualification options
    const qualificationOptions = [
        '10th',
        '12th',
        'Diploma',
        'Bachelor\'s Degree',
        'Master\'s Degree',
        'PhD',
        'Other'
    ];

    // Year of passing options (last 30 years)
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 30 }, (_, i) => (currentYear - i).toString());

    // Capture UTM parameters on component mount
    useEffect(() => {
        const utmParams = extractUTMParameters();
        if (utmParams.utm_source || utmParams.utm_medium || utmParams.utm_campaign) {
            storeUTMParameters(utmParams);
            console.log('UTM Parameters captured:', utmParams);
        } else {
            // Clear stored UTM parameters if current URL doesn't have them
            localStorage.removeItem('utm_parameters');
            console.log('No UTM parameters in URL - cleared stored UTM parameters');
        }
    }, []);

    useEffect(() => {
        if (showOtp) {
            if (!authContext.loading) {
                if (authContext.isAuthenticated) {
                    setShowOtp(false);
                    navigate(buildUrlWithUTM("/portal/dashboard"), { state: { fromRegister: true } });
                }
            }
        } else {
            if (!authContext.loading) {
                if (authContext.isAuthenticated) {
                    navigate(buildUrlWithUTM("/portal/dashboard"));
                }
            }
        }
    }, [authContext.isAuthenticated, authContext.loading, navigate, showOtp]);

    useEffect(() => {
        if (authContext.authError) {
            setError(authContext.authError);
            // Block OTP input if the error is 'We will reach out soon'
            if (authContext.authError.message === 'We will reach out soon') {
                setShowOtp(false);
            }
        }
        authContext.setAuthError(null);
    }, [authContext.authError, authContext]);

    const handleFieldChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setError(null);
    };

    const handleDateChange = (date) => {
        setFormData(prev => ({ ...prev, dateOfBirth: date }));
        setError(null);
    };

   

    const calculateAge = (dateOfBirth) => {
        if (!dateOfBirth) return null;
        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const isAllFieldsFilled = () => {
        const { name, email, phone, dateOfBirth, qualification, yearOfPassing } = formData;
        return (
            name && name.trim() &&
            email && email.trim() &&
            phone && phone.trim() &&
            dateOfBirth &&
            qualification && qualification.trim() &&
            yearOfPassing && yearOfPassing.trim()
        );
    };

    const sendRegisterOtp = () => {
        const { name, email, phone, dateOfBirth, qualification, yearOfPassing } = formData;

        // Check if all fields are filled
        if (!isAllFieldsFilled()) {
            authContext.setAuthError({ "type": "error", "message": "All Fields are Required." });
            return;
        }

        let errors = [];

        if (!name.trim()) errors.push({ "type": "error", "message": "Name is required." });
        if (!email.trim()) errors.push({ "type": "error", "message": "Email is required." });
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.push({ "type": "error", "message": "Invalid email format." });

        if (!phone.trim()) errors.push({ "type": "error", "message": "Phone Number is required." });
        else if (!/^\d{10}$/.test(phone)) errors.push({ "type": "error", "message": "Phone must be 10 digits." });

        if (!dateOfBirth) errors.push({ "type": "error", "message": "Date of Birth is required." });
        if (!qualification || !qualification.trim()) errors.push({ "type": "error", "message": "Qualification is required." });
        if (!yearOfPassing || !yearOfPassing.trim()) errors.push({ "type": "error", "message": "Year of Passing is required." });

        if (errors.length > 0) {
            console.log("Errors ", errors);
            authContext.setAuthError(errors[0]);
            return;
        }

        // Check age limit (max 28 years)
        const age = calculateAge(dateOfBirth);
        if (age !== null && age > 28) {
            Swal.fire({
                title: 'Age Limit Exceeded',
                html: 'Sorry, you are not eligible for this program. The maximum age limit is 28 years. Please check our other programs that might be suitable for you.',
                icon: 'warning',
                confirmButtonText: 'OK',
                confirmButtonColor: '#EC691F',
                customClass: {
                    popup: 'age-limit-popup',
                    title: 'age-limit-title',
                    htmlContainer: 'age-limit-message'
                }
            });
            return;
        }

        // Always extract UTM parameters from the current URL when submitting
        const utmParams = extractUTMParameters();
        
        authContext.sendRegisterOtp(formData.name, formData.email, formData.phone, utmParams).then((response) => {
            if (response) {
                console.log("OTP successfully:",);
                setShowOtp(true);
            }
        }).catch((error) => {
            console.error("Error sending OTP:", error);
        });
    };




    const handleRegister = () => {
        const { name, email, phone, dateOfBirth, qualification, yearOfPassing } = formData;

        let errors = [];

        if (!name || !name.trim()) errors.push({ "type": "error", "message": "Full Name is required." });
        if (!email || !email.trim()) errors.push({ "type": "error", "message": "Email is required." });
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.push({ "type": "error", "message": "Invalid email format." });

        if (!phone || !phone.trim()) errors.push({ "type": "error", "message": "Mobile Number is required." });
        else if (!/^\d{10}$/.test(phone)) errors.push({ "type": "error", "message": "Phone must be 10 digits." });

        if (!dateOfBirth) errors.push({ "type": "error", "message": "Date of Birth is required." });
        if (!qualification || !qualification.trim()) errors.push({ "type": "error", "message": "Qualification is required." });
        if (!yearOfPassing || !yearOfPassing.trim()) errors.push({ "type": "error", "message": "Year of Passing is required." });

        if (!otp.trim()) errors.push({ "type": "error", "message": "OTP is required." });
        else if (otp.trim().length !== 4 || !/^\d{4}$/.test(otp.trim())) errors.push({ "type": "error", "message": "OTP must be 4 digits." });

        if (errors.length > 0) {
            console.log("Errors ", errors);
            authContext.setAuthError(errors[0]);
            return;
        }

         // Always extract UTM parameters from the current URL when submitting
        const utmParams = extractUTMParameters();
        
        // Format date of birth as DD/MM/YYYY
        const formattedDateOfBirth = formData.dateOfBirth 
            ? formData.dateOfBirth.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
            : null;
        
        authContext.register(
            formData.name, 
            formData.email, 
            formData.phone, 
            otp, 
            utmParams,
            formattedDateOfBirth,
            formData.qualification,
            formData.yearOfPassing
        ).then((response) => {
            if (response) {
                console.log("Register successfully:");
                navigate('/portal/dashboard', { state: { fromRegister: true } });

            }
        }).catch((error) => {
            console.error("Register Failed:", error);
        });
    }

    return (
        <>
            <PortalHeader >
                <nav className="nav-links">
                    <a href={buildUrlWithUTM("/portal")} className="nav-link" onClick={(e) => { e.preventDefault(); navigate(buildUrlWithUTM('/portal')); }}>Home</a>
                </nav>
                <button className="logout-button" onClick={() => navigate(buildUrlWithUTM('/portal/login'))}>Login</button>
            </PortalHeader>
            {authLoading ? <LoadingComponent /> : null}
            {/* {error && <Alert error={error} onClose={() => setError(null)} />} */}
            <div className="portal-container">
                <div className="portal-form-container">
                    <div className="portal-form-card">
                    <div className='portal-form-title'>
                            Interview Registration
                        </div>
                        <div className="portal-form-description">
                            Fill out the form below and receive your personal AI interview link via email.
                    </div>
                        <div className="portal-form-divider"></div>
                        <form>
                            <div className="portal-form-grid">
                                <div>
                                    <label className="portal-form-label">
                                        Full Name <span className="required-asterisk">*</span>
                                    </label>
                                    <CustomTextField 
                                        label="Full Name" 
                                        name="name" 
                                        type="text" 
                                        onChange={handleFieldChange}
                                        value={formData.name}
                                    />
                                </div>
                                <div>
                                    <label className="portal-form-label">
                                        Mobile Number <span className="required-asterisk">*</span>
                                    </label>
                            <CustomTextField
                                label="Mobile Number"
                                        name="phone" 
                                        type="text"
                                onChange={handleFieldChange}
                                        value={formData.phone}
                                tail={true}
                                        tailContent={
                                            <div 
                                                className={`sendOtpButton ${(error && error.message === 'We will reach out soon') ? 'disabled' : ''}`}
                                                onClick={(error && error.message === 'We will reach out soon') ? undefined : sendRegisterOtp}
                                                style={{ 
                                                    pointerEvents: (error && error.message === 'We will reach out soon') ? 'none' : 'auto', 
                                                    opacity: (error && error.message === 'We will reach out soon') ? 0.5 : 1,
                                                    cursor: (error && error.message === 'We will reach out soon') ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                GET OTP
                                            </div>
                                        }
                                    />
                                </div>
                            </div>
                            <div className="portal-form-grid">
                                <div>
                                    <label className="portal-form-label">
                                        Email <span className="required-asterisk">*</span>
                                    </label>
                                    <CustomTextField 
                                        label="Email" 
                                        name="email" 
                                        type="email" 
                                        onChange={handleFieldChange}
                                        value={formData.email}
                                    />
                                </div>
                                <div>
                                    <label className="portal-form-label">
                                        Date of Birth <span className="required-asterisk">*</span>
                                    </label>
                                    <div className="custom-text-field">
                                        <DatePicker
                                            selected={formData.dateOfBirth}
                                            onChange={handleDateChange}
                                            dateFormat="dd/MM/yyyy"
                                            placeholderText="Select your DOB"
                                            className="input-field"
                                            maxDate={new Date()}
                                            showYearDropdown
                                            showMonthDropdown
                                            dropdownMode="select"
                                            isClearable
                                            todayButton="Today"
                                            calendarClassName="custom-datepicker-calendar"
                                            popperClassName="custom-datepicker-popper"
                                            popperPlacement="bottom-start"
                                            openToDate={formData.dateOfBirth || new Date()}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="portal-form-grid">
                                <div>
                                    <label className="portal-form-label">
                                        Highest Qualification <span className="required-asterisk">*</span>
                                    </label>
                                    <CustomDropdown
                                        name="qualification"
                                        options={qualificationOptions}
                                        value={formData.qualification}
                                        onChange={handleFieldChange}
                                        required={true}
                                        placeholder="Highest Qualification"
                                    />
                                </div>
                                <div>
                                    <label className="portal-form-label">
                                        Year of Passing <span className="required-asterisk">*</span>
                                    </label>
                                    <CustomDropdown
                                        name="yearOfPassing"
                                        options={yearOptions}
                                        value={formData.yearOfPassing}
                                        onChange={handleFieldChange}
                                        required={true}
                                        placeholder="Year of Passing"
                                    />
                                </div>
                            </div>

                            {showOtp && <div className='otp-container'>
                                <div className='enter-otp-text'>Enter OTP sent to your mobile number</div>
                                <OtpInput length={4} onChange={(val) => setOtp(val)} />
                            </div>}
                            <div className='portal-btn' onClick={handleRegister}>Register</div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
}

export default PortalRegisterPage;